import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { UploadedFile } from "../../models/file"
import { uploadFile, listFiles, deleteFile } from "../api/files-api"
import { RootState } from "../store"

export type UploadType = "user" | "tenant"

interface FileState {
    files: UploadedFile[]
    uploading: Partial<Record<UploadType, boolean>>
    uploadError: Partial<Record<UploadType, string | null>>
    loading: boolean
    error: string | null
    deletingIds: Record<string, boolean>
}

const initialState: FileState = {
    files: [],
    uploading: {},
    uploadError: {},
    loading: false,
    error: null,
    deletingIds: {},
}

function uploadTypeOf(arg: { uploadType?: UploadType } | undefined): UploadType {
    return arg?.uploadType ?? "user"
}

export const fileSlice = createSlice({
    name: "files",
    initialState,
    reducers: {
        clearUploadError: (state, action: PayloadAction<UploadType>) => {
            state.uploadError[action.payload] = null
        },
        clearFiles: () => initialState,
        updateFile: (state, action: PayloadAction<UploadedFile>) => {
            const index = state.files.findIndex((f) => f.id === action.payload.id)
            if (index !== -1) {
                state.files[index] = action.payload
            }
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(uploadFile.pending, (state, action) => {
                const uploadType = uploadTypeOf(action.meta.arg)
                state.uploading[uploadType] = true
                state.uploadError[uploadType] = null
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                const uploadType = uploadTypeOf(action.meta.arg)
                state.uploading[uploadType] = false
                if (action.payload.status === 201 && action.payload.data) {
                    state.files.unshift(action.payload.data)
                } else if (action.payload.error) {
                    state.uploadError[uploadType] = action.payload.error
                }
            })
            .addCase(uploadFile.rejected, (state, action) => {
                const uploadType = uploadTypeOf(action.meta.arg)
                state.uploading[uploadType] = false
                state.uploadError[uploadType] = "Failed to upload file"
            })

            .addCase(listFiles.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(listFiles.fulfilled, (state, action) => {
                state.loading = false
                if (action.payload.status === 200) {
                    state.files = action.payload.data
                } else if (action.payload.error) {
                    state.error = action.payload.error
                }
            })
            .addCase(listFiles.rejected, (state) => {
                state.loading = false
                state.error = "Failed to load files"
            })

            .addCase(deleteFile.pending, (state, action) => {
                state.deletingIds[action.meta.arg.fileId] = true
            })
            .addCase(deleteFile.fulfilled, (state, action) => {
                delete state.deletingIds[action.meta.arg.fileId]
                if (action.payload.status === 204) {
                    state.files = state.files.filter((f) => f.id !== action.meta.arg.fileId)
                }
            })
            .addCase(deleteFile.rejected, (state, action) => {
                delete state.deletingIds[action.meta.arg.fileId]
            }),
})

export const { clearUploadError, clearFiles, updateFile } = fileSlice.actions
export default fileSlice.reducer

export const selectFiles = (state: RootState) => state.files?.files ?? []
export const selectFilesLoading = (state: RootState) => state.files?.loading ?? false
export const selectFilesUploading =
    (uploadType: UploadType) =>
    (state: RootState): boolean =>
        state.files?.uploading?.[uploadType] ?? false
export const selectFilesError = (state: RootState) => state.files?.error ?? null
export const selectUploadError =
    (uploadType: UploadType) =>
    (state: RootState): string | null =>
        state.files?.uploadError?.[uploadType] ?? null
export const selectDeletingIds = (state: RootState): Record<string, boolean> =>
    state.files?.deletingIds ?? {}
